<script setup>
import { computed, ref, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
// 'main' shows the overflow actions; 'team' shows the team picker sub-menu.
const activeMenu = ref('main');

const currentChat = computed(() => store.getters.getSelectedChat);
const teams = computed(() => store.getters['teams/getTeams']);
const assignedTeam = computed(() => currentChat.value?.meta?.team);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  items.push({
    icon: 'i-lucide-users',
    label: t('CONVERSATION.HEADER.ASSIGN_TEAM'),
    action: 'assign_team',
    value: 'assign_team',
  });

  return items;
});

const teamMenuItems = computed(() => {
  const items = [
    {
      icon: 'i-lucide-arrow-left',
      label: t('CONVERSATION.HEADER.ASSIGN_TEAM_BACK'),
      action: 'back',
      value: 'back',
    },
  ];

  if (assignedTeam.value) {
    items.push({
      icon: 'i-lucide-x',
      label: t('TEAMS_SETTINGS.LIST.NONE'),
      action: 'select_team',
      value: 0,
    });
  }

  teams.value.forEach(team => {
    items.push({
      icon: 'i-lucide-users',
      label: team.name,
      action: 'select_team',
      value: team.id,
      isSelected: assignedTeam.value?.id === team.id,
    });
  });

  return items;
});

const assignTeam = teamId => {
  const conversationId = currentChat.value.id;
  const team = teams.value.find(item => item.id === teamId) || null;
  store.dispatch('setCurrentChatTeam', { team, conversationId });
  store
    .dispatch('assignTeam', { conversationId, teamId })
    .then(() => useAlert(t('CONVERSATION.CHANGE_TEAM')));
};

const onToggleDropdown = () => {
  toggleDropdown();
  activeMenu.value = 'main';
};

const handleActionClick = ({ action, value }) => {
  if (action === 'assign_team') {
    activeMenu.value = 'team';
    return;
  }
  if (action === 'back') {
    activeMenu.value = 'main';
    return;
  }

  toggleDropdown(false);
  activeMenu.value = 'main';

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'select_team') {
    assignTeam(value);
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="onToggleDropdown"
      />
      <DropdownMenu
        v-if="showActionsDropdown && activeMenu === 'main'"
        :menu-items="actionMenuItems"
        class="mt-1 top-full ltr:left-0 rtl:right-0 xl:ltr:left-auto xl:ltr:right-0 xl:rtl:right-auto xl:rtl:left-0"
        @action="handleActionClick"
      />
      <DropdownMenu
        v-else-if="showActionsDropdown && activeMenu === 'team'"
        :menu-items="teamMenuItems"
        show-search
        :search-placeholder="$t('CONVERSATION.HEADER.ASSIGN_TEAM_SEARCH')"
        class="mt-1 top-full max-h-80 max-w-[210px] ltr:left-0 rtl:right-0 xl:ltr:left-auto xl:ltr:right-0 xl:rtl:right-auto xl:rtl:left-0"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
